/**
 * Helpers for the site-layout (header/footer) editor: HTML5 drag-and-drop plumbing for its many
 * reorderable lists, in-place normalizers that guarantee every nested collection and href exists,
 * the mapping between the editor model (SiteSettingsModel) and the save payload
 * (SiteSettingsPayload), and : at the bottom : the tolerant importer behind the raw-JSON editing
 * mode.
 */
import type React from "react";
import type {
	AddressBlock,
	HeaderNavItem,
	LinkGroup,
	NavLink,
	SiteSettings as SiteSettingsModel,
	SiteSettingsPayload,
	SocialLink
} from "../../types/layout";

/** One drag "channel" per reorderable list. The `${number}`-suffixed variants bind nested lists
 *  to their parent's index, so a child link dragged out of nav item 2 cannot be dropped into nav
 *  item 3's children : handleLayoutDrop compares the full type string before reordering. */
export type LayoutDragType =
	| "locales"
	| "quick-links"
	| "nav-items"
	| `nav-children-${number}`
	| "footer-addresses"
	| `address-lines-${number}`
	| "footer-groups"
	| `group-links-${number}`
	| "footer-audiences"
	| "footer-socials"
	| "footer-legal";

// Custom MIME type: drags that don't originate from one of our handles (text selections, files,
// links from outside the app) carry no payload under this key and are silently ignored on drop.
export const LAYOUT_DRAG_MIME = "application/x-layout-dnd";

export const beginLayoutDrag = (event: React.DragEvent, type: LayoutDragType, index: number) => {
	event.dataTransfer.effectAllowed = "move";
	event.dataTransfer.setData(LAYOUT_DRAG_MIME, JSON.stringify({ type, index }));
};

export const allowLayoutDrop = (event: React.DragEvent) => event.preventDefault();

export type LayoutDropHandler = (
	event: React.DragEvent,
	type: LayoutDragType,
	targetIndex: number,
	reorder: (from: number, to: number) => void
) => void;

export const handleLayoutDrop: LayoutDropHandler = (event, type, targetIndex, reorder) => {
	event.preventDefault();
	const raw = event.dataTransfer.getData(LAYOUT_DRAG_MIME);
	if (!raw) return;
	try {
		const payload = JSON.parse(raw) as { type: LayoutDragType; index: number };
		if (payload.type !== type || typeof payload.index !== "number") return;
		reorder(payload.index, targetIndex);
	} catch {
		// ignore malformed payloads
	}
};

export interface DragHelpers {
	beginDrag: typeof beginLayoutDrag;
	allowDrop: typeof allowLayoutDrop;
	handleDrop: LayoutDropHandler;
}

export const deepCloneLayout = <T,>(value: T): T => JSON.parse(JSON.stringify(value ?? {}));

export const reorderList = <T,>(items: T[], from: number, to: number): T[] => {
	const list = [...items];
	if (from < 0 || from >= list.length) {
		return list;
	}

	const [moved] = list.splice(from, 1);
	const targetIndex = Math.max(0, Math.min(to, list.length));
	list.splice(targetIndex, 0, moved);
	return list;
};

export const createEmptyNavLink = (): NavLink => ({ label: "", href: { slug: "" } });
export const createEmptyHeaderNavItem = (): HeaderNavItem => ({ label: "", href: { slug: "" }, children: [] });
export const createEmptyAddressBlock = (): AddressBlock => ({ title: "", lines: [], actions: [] });
export const createEmptyLinkGroup = (): LinkGroup => ({ title: "", links: [] });
export const createEmptySocialLink = () => ({ label: "", href: { slug: "" }, icon: "" });

export const createEmptySettings = (language: string): SiteSettingsModel => ({
	language,
	isDisabled: false,
	header: {
		logoAlt: "",
		quickLinksLabel: "",
		quickLinks: [],
		nav: []
	},
	footer: {
		addresses: [],
		groups: [],
		audiences: [],
		socials: [],
		legalLinks: []
	}
});

// Canonical href shape: an external URL wins outright; otherwise only the keys that are actually
// set are kept. A fully empty target deliberately collapses to { slug: "" } rather than being
// dropped : the public sites treat that exact shape as "not configured yet" (placeholder link on
// preview, hidden on production), so it must survive every save round-trip.
const normalizeHrefTarget = (href?: NavLink["href"]) => {
	if (!href) return { slug: "" };
	const slug = (href.slug ?? "").toString().trim();
	const external = (href.external ?? "").toString().trim();
	const pageId = (href.pageId ?? "").toString().trim();

	if (external) {
		return { external };
	}

	const result: { slug?: string; pageId?: string; external?: string } = {};
	if (slug) result.slug = slug;
	if (pageId) result.pageId = pageId;

	if (!result.slug && !result.pageId) {
		return { slug: "" };
	}

	return result;
};

// ensureHeader/ensureFooter normalize *in place*: every list exists and every href is in canonical
// shape afterwards, which lets the editor UI index into the structure without null guards. Callers
// that must not mutate their input clone first (see sanitizeLayoutSettings).
export const ensureHeader = (settings: SiteSettingsModel) => {
	settings.header ??= { logoAlt: "", quickLinksLabel: "", quickLinks: [], nav: [] };
	settings.header.logoAlt ??= "";
	settings.header.quickLinksLabel ??= "";
	settings.header.quickLinks = (settings.header.quickLinks ?? []).map((link) => ({
		label: link?.label ?? "",
		href: normalizeHrefTarget(link?.href),
		isDisabled: link?.isDisabled === true
	}));
	settings.header.nav ??= [];
	settings.header.nav = settings.header.nav.map((item) => ({
		label: item?.label ?? "",
		href: normalizeHrefTarget(item?.href),
		isDisabled: item?.isDisabled === true,
		children: (item?.children ?? []).map((child) => ({
			label: child?.label ?? "",
			href: normalizeHrefTarget(child?.href),
			isDisabled: child?.isDisabled === true
		}))
	}));
	return settings.header;
};

export const ensureFooter = (settings: SiteSettingsModel) => {
	settings.footer ??= { addresses: [], groups: [], audiences: [], socials: [], legalLinks: [] };
	settings.footer.addresses ??= [];
	settings.footer.groups ??= [];
	settings.footer.audiences ??= [];
	settings.footer.socials ??= [];
	settings.footer.legalLinks ??= [];
	settings.footer.addresses = settings.footer.addresses.map((address) => ({
		title: address?.title ?? "",
		lines: Array.isArray(address?.lines) ? address.lines : [],
		actions: (address?.actions ?? []).map((action) => ({
			label: action?.label ?? "",
			href: normalizeHrefTarget(action?.href)
		}))
	}));
	settings.footer.groups = settings.footer.groups.map((group) => ({
		title: group?.title ?? "",
		links: (group?.links ?? []).map((link) => ({
			label: link?.label ?? "",
			href: normalizeHrefTarget(link?.href)
		}))
	}));
	settings.footer.audiences = (settings.footer.audiences ?? []).map((link) => ({
		label: link?.label ?? "",
		href: normalizeHrefTarget(link?.href)
	}));
	settings.footer.socials = (settings.footer.socials ?? []).map((link) => ({
		label: link?.label ?? "",
		href: normalizeHrefTarget(link?.href),
		icon: link?.icon ?? ""
	}));
	settings.footer.legalLinks = (settings.footer.legalLinks ?? []).map((link) => ({
		label: link?.label ?? "",
		href: normalizeHrefTarget(link?.href)
	}));
	return settings.footer;
};

export const sanitizeLayoutSettings = (settings: SiteSettingsModel): SiteSettingsModel => {
	const next = deepCloneLayout(settings);
	next.language = (next.language ?? "").trim().toLowerCase();
	ensureHeader(next);
	ensureFooter(next);
	return next;
};

export const buildLayoutPayloadFromSettings = (settings: SiteSettingsModel | null): SiteSettingsPayload | null => {
	if (!settings) return null;
	const header = ensureHeader(settings);
	const footer = ensureFooter(settings);

	return {
		header: {
			logoAlt: header.logoAlt ?? "",
			quickLinksLabel: header.quickLinksLabel ?? "",
			quickLinks: (header.quickLinks ?? []).map((link) => ({
				label: link?.label ?? "",
				href: normalizeHrefTarget(link?.href),
				isDisabled: link?.isDisabled === true
			})),
			nav: (header.nav ?? []).map((item) => ({
				label: item?.label ?? "",
				href: normalizeHrefTarget(item?.href),
				isDisabled: item?.isDisabled === true,
				children: (item?.children ?? []).map((child) => ({
					label: child?.label ?? "",
					href: normalizeHrefTarget(child?.href),
					isDisabled: child?.isDisabled === true
				}))
			}))
		},
		footer: {
			addresses: (footer.addresses ?? []).map((address) => ({
				title: address?.title ?? "",
				lines: [...(address?.lines ?? [])],
				actions: (address?.actions ?? []).map((action) => ({
					label: action?.label ?? "",
					href: normalizeHrefTarget(action?.href)
				}))
			})),
			groups: (footer.groups ?? []).map((group) => ({
				title: group?.title ?? "",
				links: (group?.links ?? []).map((link) => ({
					label: link?.label ?? "",
					href: normalizeHrefTarget(link?.href)
				}))
			})),
			audiences: (footer.audiences ?? []).map((link) => ({
				label: link?.label ?? "",
				href: normalizeHrefTarget(link?.href)
			})),
			socials: (footer.socials ?? []).map((link) => ({
				label: link?.label ?? "",
				href: normalizeHrefTarget(link?.href),
				icon: link?.icon ?? ""
			})),
			legalLinks: (footer.legalLinks ?? []).map((link) => ({
				label: link?.label ?? "",
				href: normalizeHrefTarget(link?.href)
			}))
		}
	};
};

// Dirty-check for the save button. JSON comparison is reliable here because both sides come from
// buildLayoutPayloadFromSettings, which emits keys in a deterministic order.
export const layoutPayloadsEqual = (left: SiteSettingsPayload | null, right: SiteSettingsPayload | null) =>
	JSON.stringify(left ?? null) === JSON.stringify(right ?? null);

const coerceString = (value: unknown, fallback = ""): string => {
	if (typeof value === "string") return value;
	if (typeof value === "number" || typeof value === "boolean") return String(value);
	return fallback;
};

const coerceBool = (value: unknown): boolean => {
	if (typeof value === "boolean") return value;
	if (typeof value === "string") {
		const normalized = value.trim().toLowerCase();
		if (["true", "1", "yes"].includes(normalized)) return true;
		if (["false", "0", "no"].includes(normalized)) return false;
	}
	if (typeof value === "number") {
		if (value === 1) return true;
		if (value === 0) return false;
	}
	return false;
};

// Import shorthand: hrefs given as bare strings are classified by protocol (absolute URL →
// external, everything else → leading-slash slug). Non-string values fall through coerceString to
// the empty "unconfigured" target.
const mapHrefTarget = (value: unknown): NavLink["href"] => {
	const str = coerceString(value, "").trim();
	if (!str) return { slug: "" };
	if (/^https?:\/\//i.test(str)) return { external: str };
	return { slug: str.startsWith("/") ? str : `/${str}` };
};

const mapNavLinks = (value: unknown): NavLink[] => {
	if (!Array.isArray(value)) return [];
	return value
		.map((item) => ({
			label: coerceString((item as NavLink)?.label),
			href: mapHrefTarget((item as NavLink)?.href),
			isDisabled: coerceBool((item as NavLink)?.isDisabled)
		}))
		.filter((link) => link.label.length > 0);
};

const mapNavItems = (value: unknown): HeaderNavItem[] => {
	if (!Array.isArray(value)) return [];
	return value.map((item) => ({
		label: coerceString((item as HeaderNavItem)?.label),
		href: mapHrefTarget((item as HeaderNavItem)?.href),
		isDisabled: coerceBool((item as HeaderNavItem)?.isDisabled),
		children: mapNavLinks((item as HeaderNavItem)?.children)
	}));
};

const mapAddresses = (value: unknown): AddressBlock[] => {
	if (!Array.isArray(value)) return [];
	return value.map((entry) => ({
		title: coerceString((entry as AddressBlock)?.title),
		lines: Array.isArray((entry as AddressBlock)?.lines)
			? ((entry as AddressBlock)?.lines ?? []).map((line) => coerceString(line))
			: [],
		actions: mapNavLinks((entry as AddressBlock)?.actions)
	}));
};

const mapGroups = (value: unknown): LinkGroup[] => {
	if (!Array.isArray(value)) return [];
	return value.map((entry) => ({
		title: coerceString((entry as LinkGroup)?.title),
		links: mapNavLinks((entry as LinkGroup)?.links)
	}));
};

const mapSocials = (value: unknown) => {
	if (!Array.isArray(value)) return [];
	return value.map((entry) => ({
		label: coerceString((entry as SocialLink)?.label),
		href: mapHrefTarget((entry as SocialLink)?.href),
		icon: coerceString((entry as SocialLink)?.icon)
	}));
};

// Raw-JSON import path: coerces a hand-pasted blob into a full SiteSettingsModel, defaulting every
// missing section and dropping unknown keys, then runs it through the same sanitizer as normal
// edits. Only the top-level shape is a hard error; individual malformed entries degrade to empty
// strings/lists instead of failing the whole import.
export const parseLayoutJson = (raw: unknown, language: string): SiteSettingsModel => {
	if (!raw || typeof raw !== "object" || Array.isArray(raw)) {
		throw new Error("Layout JSON must be an object with header and footer sections.");
	}

	const headerSection = (raw as Record<string, unknown>).header ?? {};
	const footerSection = (raw as Record<string, unknown>).footer ?? {};

	const candidate: SiteSettingsModel = {
		language,
		isDisabled: coerceBool((raw as Record<string, unknown>).isDisabled),
		header: {
			logoAlt: coerceString((headerSection as Record<string, unknown>).logoAlt),
			quickLinksLabel: coerceString((headerSection as Record<string, unknown>).quickLinksLabel),
			quickLinks: mapNavLinks((headerSection as Record<string, unknown>).quickLinks),
			nav: mapNavItems((headerSection as Record<string, unknown>).nav)
		},
		footer: {
			addresses: mapAddresses((footerSection as Record<string, unknown>).addresses),
			groups: mapGroups((footerSection as Record<string, unknown>).groups),
			audiences: mapNavLinks((footerSection as Record<string, unknown>).audiences),
			socials: mapSocials((footerSection as Record<string, unknown>).socials),
			legalLinks: mapNavLinks((footerSection as Record<string, unknown>).legalLinks)
		}
	};

	return sanitizeLayoutSettings(candidate);
};

